import { baseApi } from 'utils/apiUtil';
import { http } from 'utils/httpUtil';
import OriginalImageCropParams from 'model/OriginalImageCropParams';

const benguideApiV2 = `${baseApi}/v2/benefit-guides`;

export interface ImageReferenceData {
  '@type': 'imageReferenceData';
  imageReference: string;
  originalImageReference: string;
  originalImageCropParams: {
    width: number;
    height: number;
    x: number;
    y: number;
  };
  defaultImageReference: string | null;
}

export interface CustomPageSectionVO {
  id?: string;
  mediaType: 'RICH_MEDIA' | 'IMAGE' | 'CONTENT_CARD';
  orderNo: number;
  imageReference?: string;
  content?: string;
  title?: string;
  referenceData?: ImageReferenceData | null;
}

export interface CustomPageVO {
  id?: string;
  name: string;
  pageSections: CustomPageSectionVO[];
}

export interface BenefitGuideResponse {
  id: string;
  masterId: string;
  customPages?: CustomPageVO[];
}

export interface UploadedImageVO {
  imageReference: string;
  latestRevision?: number;
}

export const createCustomPage = async (
  benefitGuideId: string,
  customPageData: CustomPageVO
): Promise<CustomPageVO> => {
  try {
    const response = await http.post(
      `${benguideApiV2}/${benefitGuideId}/custom-pages`,
      customPageData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating custom page:', error);
    throw error;
  }
};

export const getCustomPages = async (
  benefitGuideId: string
): Promise<BenefitGuideResponse> => {
  try {
    const response = await http.get(`${benguideApiV2}/${benefitGuideId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching custom pages:', error);
    throw error;
  }
};

export const updateCustomPage = async (
  benefitGuideId: string,
  customPageId: string,
  customPageData: CustomPageVO,
  validateNameChange: boolean = false,
  updateSections: boolean = true
): Promise<CustomPageVO> => {
  try {
    const response = await http.put(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/${customPageId}?validateNameChange=${validateNameChange}&updateSections=${updateSections}`,
      customPageData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating custom page:', error);
    throw error;
  }
};

export const deleteCustomPage = async (
  benefitGuideId: string,
  customPageId: string
): Promise<BenefitGuideResponse> => {
  try {
    const response = await http.delete(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/${customPageId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting custom page:', error);
    throw error;
  }
};

export const deleteCustomPageSection = async (
  benefitGuideId: string,
  customPageId: string,
  customPageSectionId: string
): Promise<{ latestRevision: number }> => {
  try {
    const response = await http.delete(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/${customPageId}/sections/${customPageSectionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting custom page section:', error);
    throw error;
  }
};

export const mapComponentToMediaType = (
  componentId: string
): 'RICH_MEDIA' | 'IMAGE' | 'CONTENT_CARD' => {
  switch (componentId) {
    case 'large-text-field':
      return 'RICH_MEDIA';
    case 'image-block':
      return 'IMAGE';
    case 'content-card':
      return 'CONTENT_CARD';
    default:
      return 'RICH_MEDIA';
  }
};

export const createPageSections = (
  selectedComponents: any[]
): CustomPageSectionVO[] => {
  if (!selectedComponents || !Array.isArray(selectedComponents)) {
    return [];
  }

  const sections: CustomPageSectionVO[] = [];
  let orderCount = 0;

  selectedComponents.forEach((component) => {
    const mediaType = mapComponentToMediaType(component.id);
    const count = Number(component.count) || 0;

    for (let i = 0; i < count; i++) {
      orderCount++;

      sections.push({
        mediaType,
        orderNo: orderCount,
      });
    }
  });

  return sections;
};

export const updateCustomPageSectionOrder = async (
  benefitGuideId: string,
  customPageId: string,
  customPageSectionId: string,
  newOrderNo: number
): Promise<void> => {
  try {
    await http.put(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/${customPageId}/sections/${customPageSectionId}/order`,
      { newOrderNo }
    );
  } catch (error) {
    console.error('Error updating custom page section order:', error);
    throw error;
  }
};

export const uploadCustomPageImage = async (
  benefitGuideId: string,
  image: Blob,
  customPageId: string,
  customPageSectionId: string,
  original?: boolean,
  originalRef?: string,
  cropArea?: OriginalImageCropParams
): Promise<UploadedImageVO> => {
  try {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('custom-page-id', customPageId);
    formData.append('custom-page-section-id', customPageSectionId);

    if (originalRef) {
      formData.append('original-reference', originalRef);
    }

    if (cropArea) {
      formData.append('height', cropArea.height.toString());
      formData.append('width', cropArea.width.toString());
      formData.append('x', cropArea.x.toString());
      formData.append('y', cropArea.y.toString());
    }

    if (original) {
      formData.append('original', 'true');
    }

    const response = await http.put(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading custom page image:', error);
    throw error;
  }
};

export const getCustomPageImageContent = (
  benefitGuideId: string,
  customPageId: string,
  customPageSectionId: string,
  original: boolean = false,
  revision?: number
): string => {
  const params = new URLSearchParams({
    'custom-page-id': customPageId,
    'custom-page-section-id': customPageSectionId,
    original: original.toString(),
  });

  if (revision !== undefined) {
    params.set('revision', revision.toString());
  }

  return `${benguideApiV2}/${benefitGuideId}/custom-pages/images?${params.toString()}`;
};

export const removeCustomPageImageContent = async (
  benefitGuideId: string,
  customPageId: string,
  customPageSectionId: string
): Promise<{ latestRevision: number }> => {
  try {
    const params = new URLSearchParams({
      'custom-page-id': customPageId,
      'custom-page-section-id': customPageSectionId,
    });

    const response = await http.delete(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/images?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Error removing custom page image content:', error);
    throw error;
  }
};

export interface CustomPageRichMediaContentVO {
  customPageId: string;
  customPageSectionId: string;
  content: string;
  title?: string;
  mediaType?: 'RICH_MEDIA' | 'CONTENT_CARD';
}

export const getCustomPageRichMediaContent = async (
  benefitGuideId: string,
  customPageId: string,
  customPageSectionId: string
): Promise<{ content: string; title: string }> => {
  try {
    const params = new URLSearchParams({
      'custom-page-id': customPageId,
      'custom-page-section-id': customPageSectionId,
    });

    const response = await http.get(
      `${benguideApiV2}/${benefitGuideId}/custome-pages/rich-media?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching custom page rich media content:', error);
    throw error;
  }
};

export const updateCustomPageRichMediaContent = async (
  benefitGuideId: string,
  data: CustomPageRichMediaContentVO
): Promise<{ latestRevision: number }> => {
  try {
    const requestData = {
      customPageId: data.customPageId,
      customPageSectionId: data.customPageSectionId,
      content: data.content,
      title: data.title || '',
      mediaType: data.mediaType || 'RICH_MEDIA',
    };

    const response = await http.put(
      `${benguideApiV2}/${benefitGuideId}/custom-pages/rich-media`,
      requestData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating custom page rich media content:', error);
    throw error;
  }
};
